import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Stack,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS, WEEKDAY_ENUM, WEEKDAY_MAP } from '../../../../utils/constants.tsx'
import { useAppSelector } from '../../../../utils/custom-hooks/useTypedReduxStore.ts'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { getDateFormatPatternFromLocale } from 'utils/helpers'
import dayjs from 'dayjs'

const RecurringDispatchTicketModal = ({ isOpen, onSave, onDelete, isEditable, onHideModal, recurringData }) => {
  const [interval, setInterval] = useState<string>('1')
  const [frequency, setFrequency] = useState<string>(RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.WEEKLY.id)
  const [dayOfTheWeek, setDayOfTheWeek] = useState<WEEKDAY_ENUM[]>([WEEKDAY_MAP[new Date().getDay()]])
  const [endType, setEndType] = useState<'never' | 'on'>('never')
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [includeDriver, setIncludeDriver] = useState<boolean>(false)
  const { voidRecurringDt } = useAppSelector(state => state.featureFlags?.variables || {})
  const { locale } = useAppSelector(state => state.auth)
  const { dateFormat, muiDateFormat } = getDateFormatPatternFromLocale(locale)

  // This effect updates the form with the existing data, if any
  useEffect(() => {
    if (recurringData) {
      setInterval((recurringData.recurrenceRule?.interval ?? interval).toString())
      setFrequency(recurringData.recurrenceRule?.frequency ?? frequency)
      setDayOfTheWeek(recurringData.recurrenceRule?.dayOfTheWeek ?? dayOfTheWeek)
      setEndType('on')
      setEndDate(recurringData.endDate ?? endDate)
      setIncludeDriver(recurringData.includeDriver ?? includeDriver)
    }
  }, [recurringData])

  const handleDayToggle = dayValue => {
    if (dayOfTheWeek.includes(dayValue)) {
      setDayOfTheWeek(dayOfTheWeek.filter(d => d !== dayValue))
    } else {
      setDayOfTheWeek([...dayOfTheWeek, dayValue])
    }
  }

  const handleSave = () => {
    onSave({
      includeDriver,
      endDate: endType === 'on' ? endDate || undefined : undefined,
      frequency: frequency.toUpperCase(),
      interval: parseInt(interval, 10),
      dayOfTheWeek: frequency === RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.WEEKLY.id ? dayOfTheWeek : undefined,
    })
    onHideModal()
  }

  return (
    <Dialog open={isOpen} onClose={onHideModal} aria-labelledby="recurrence-modal-title">
      <DialogTitle id="recurrence-modal-title">Recurring Schedule</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Schedule Interval */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <FormControl variant="standard" sx={{ width: '100px' }}>
              <TextField
                type="number"
                label="Repeat every"
                variant="standard"
                value={interval}
                disabled={!isEditable}
                sx={{
                  '& .MuiInputLabel-root.Mui-disabled': {
                    color: '#4B4B4B',
                  },
                }}
                onKeyDown={e => {
                  if (e.key === '.') {
                    e.preventDefault()
                  }
                }}
                onChange={e => setInterval(e.target.value)}
                onBlur={() => {
                  // if they leave it empty, set back to '1'
                  if (!interval || parseInt(interval, 10) < 1) {
                    setInterval('1')
                  }
                }}
              />
            </FormControl>
            <FormControl variant="standard">
              <InputLabel id="frequency-select-label">Unit</InputLabel>
              <Select
                labelId="frequency-select-label"
                value={frequency}
                onChange={e => setFrequency(e.target.value)}
                label="Unit"
                disabled={!isEditable}
              >
                <MenuItem
                  value={RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.DAILY.id}
                >{`${parseInt(interval) > 1 ? 'Days' : 'Day'}`}</MenuItem>
                <MenuItem
                  value={RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.WEEKLY.id}
                >{`${parseInt(interval) > 1 ? 'Weeks' : 'Week'}`}</MenuItem>
                <MenuItem
                  value={RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.MONTHLY.id}
                >{`${parseInt(interval) > 1 ? 'Months' : 'Month'}`}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Weekly Schedule */}
          {frequency === RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.WEEKLY.id && (
            <Box>
              <FormLabel component="legend" sx={{ mb: 1.5 }}>
                Schedule Days
              </FormLabel>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {Object.values(WEEKDAY_ENUM).map(d => (
                  <Box key={d} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {d}
                    </Typography>
                    <Checkbox
                      checked={dayOfTheWeek.includes(d)}
                      onChange={() => handleDayToggle(d)}
                      size="small"
                      disabled={!isEditable}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Schedule End */}
          <Box>
            <FormLabel component="legend">Schedule End</FormLabel>
            <RadioGroup value={endType} onChange={e => setEndType(e.target.value as 'never' | 'on')} sx={{ mt: 1 }}>
              <FormControlLabel value="never" control={<Radio disabled={!isEditable} />} label="Never" />
              <FormControlLabel
                value="on"
                control={<Radio disabled={!isEditable} />}
                label={
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>On</span>
                      {/* TODO: see if we can migrate to generic date picker or date picker v2 */}
                      <DatePicker
                        disabled={endType !== 'on' || !isEditable}
                        value={endDate}
                        onChange={newVal => setEndDate(newVal)}
                        minDate={new Date()}
                        inputFormat={muiDateFormat}
                        renderInput={params => {
                          const dateValue = dayjs(params.inputProps?.value, dateFormat).valueOf()
                          const inputValue =
                            !isNaN(dateValue) && dayjs(dateValue).isValid()
                              ? dateFormatter(dateValue)
                              : params.inputProps?.value ?? ''
                          return (
                            <TextField
                              {...params}
                              inputProps={{
                                ...params.inputProps,
                                value: inputValue,
                                placeholder: dateFormat,
                              }}
                              size="small"
                            />
                          )
                        }}
                      />
                    </Box>
                  </LocalizationProvider>
                }
              />
            </RadioGroup>
          </Box>

          {/* Driver Option */}
          <FormControlLabel
            control={
              <Checkbox
                checked={includeDriver}
                onChange={e => setIncludeDriver(e.target.checked)}
                disabled={!isEditable}
              />
            }
            label="Include driver in recurring tickets"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onHideModal}>
          Cancel
        </Button>
        {isEditable && (
          <Button variant="contained" onClick={handleSave}>
            Done
          </Button>
        )}
        {!isEditable && (
          <Button variant="outlined" color="error" onClick={onDelete}>
            {voidRecurringDt ? 'End Recurrence' : 'Delete Recurring Tickets'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default RecurringDispatchTicketModal
